import { toast } from "@/components/ui/use-toast";
import { tryOpenTrialCapDialog } from "@/store/trialCapStore";
import { tryOpenVerifyCardFirst } from "@/store/cardVerificationStore";
import { isCardUnverifiedError } from "@/lib/billing/cardVerification";
import { authStore, useAuthStore } from "@/store/authStore";
import { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import React from "react";
import { translate } from "./i18n-config";
import { generateRequestId } from "./requestId";

declare module "axios" {
    export interface AxiosRequestConfig {
        /**
         * Suppress the global error toast for this request. The caller is
         * responsible for surfacing the failure (e.g. into a form field).
         */
        silentError?: boolean;
        /**
         * Opt this request out of the CT-36 401 refresh-and-retry. Reserved for
         * requests whose 401 answers a question (a spent reset link, "no access")
         * rather than signaling an expired token. Regular data fetches — silent or
         * not — should NOT set this: they want the silent refresh.
         */
        skipAuthRefresh?: boolean;
        /**
         * Send this request with NO Authorization header, even when a session exists (CT-55).
         *
         * The TV route skips the boot refresh, so its tab holds no session; this is the second line.
         * Without it a tab that did hold one would send the operator's live token to public routes that
         * ignore it, on a screen that sits open in a lobby, where a DevTools look or a HAR export hands
         * it to whoever is standing there. It also sets skipAuthRefresh: a public 401 must never start
         * the refresh that would open a session on that screen.
         */
        noAuth?: boolean;
    }
}

/**
 * This is used when we have public URL and we don't know the tenant (ex: wallboards)
 */
export function getSipApiUrl() {
    return import.meta.env.NEXT_PUBLIC_SIP_API_URL;
}

/**
 * Get base path for assets and routing (VITE_BASE_PATH; empty when served from the root)
 */
export function getBasePath(): string {
    return import.meta.env.VITE_BASE_PATH || "";
}

export function getRootApiUrl() {
    return import.meta.env?.NEXT_PUBLIC_API_URL ?? "";
}

/** Stage tenant hosts are `{sub}-stage.pbx.im` (CT-318 — see pbx-api docs/CT-318-tenant-subdomain-routes.md): one label deep, so the existing `*.pbx.im`
 *  wildcard record and certificate already cover them. A subdomain is `[a-z0-9]+`, so the suffix
 *  can never be part of a real one. Stage builds only — a legacy production tenant may carry a
 *  hyphen of its own. */
const STAGE_TENANT_HOST_SUFFIX = "-stage";

export function getSubdomain(hostname: string): string {
    if (hostname === "localhost") {
        return "localhost";
    }
    const parts = hostname.split(".");
    if (parts.length > 2) {
        const label = parts[0];
        if (import.meta.env.MODE === "stage" && label.endsWith(STAGE_TENANT_HOST_SUFFIX)) {
            return label.slice(0, -STAGE_TENANT_HOST_SUFFIX.length);
        }
        return label;
    }
    return "";
}

function ensureRequestId(config: InternalAxiosRequestConfig): void {
    // Skip in local Vite dev: SIP CORS on stage doesn't whitelist x-request-id,
    // so preflight fails. Production/staging FE bundles still send it.
    if (import.meta.env.DEV) {
        return;
    }
    if (!config.headers.get("x-request-id")) {
        config.headers.set("x-request-id", generateRequestId());
    }
}

/**
 * The request interceptor every client shares: a request id, the client's base URL (resolved per
 * request, so importing a client never reads env), and the sipio-id Bearer — the ONLY credential the
 * FE presents (CT-36); pbx-api and sip.io-v2 /v1 both verify it.
 */
export function createRequestInterceptor(getBaseUrl: () => string) {
    return (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        ensureRequestId(config);
        config.baseURL = getBaseUrl();

        const token = useAuthStore.getState().authToken;
        if (config.noAuth) {
            // no credential of any kind: no cross-origin cookie session either (a same-origin cookie still
            // rides and the public routes ignore it), and a 401 on a request that carried nothing cannot
            // mean "expired token", so it must not start the refresh
            config.withCredentials = false;
            config.skipAuthRefresh = true;
        } else if (token && !config.headers.get("Authorization")) {
            config.headers.set("Authorization", `Bearer ${token}`);
        }

        return config;
    };
}

/**
 * URL fragments whose callers show some refusals themselves; new calls should pass `silentError` instead.
 * - docs search / assistant: no toast for 401 or 403 and no logout; the panel says so.
 * - onboarding codes and profile mobile: 400/422 (and 401/403 detail) render on the form.
 * - profile mobile: 429 and 5xx render inline with their own countdown (CT-130).
 */
const SELF_HANDLED_URLS = ["/docs"];
const INLINE_VALIDATION_URLS = [
    "onboarding/verify-email-code",
    "onboarding/verify-phone-code",
    "onboarding/send-email-code",
    "onboarding/send-phone-code",
    "profile/mobile/",
];
const INLINE_LIMIT_URLS = ["/profile/mobile/"];

const DEFAULT_RETRY_AFTER_SEC = 3600;

type Notice = { title: string; message: string; inline?: boolean };

/** The text for a Worker refusal — rest-kit's `{ ok: false, error, details?, message? }`. */
function refusalMessage(data: any): string {
    if (typeof data?.message === "string" && data.message) return data.message;
    if (Array.isArray(data?.details)) {
        const lines = data.details
            .map((detail: { path?: string; message?: string }) => detail?.message)
            .filter((message: unknown): message is string => typeof message === "string" && message !== "");
        if (lines.length) return lines.join("\n");
    }
    if (typeof data?.reason === "string" && data.reason) return data.reason;
    return typeof data?.error === "string" ? data.error : "";
}

/** Seconds until a 429 lifts: the body's retry_after, else the Retry-After header, else the reset stamp. */
function retryAfterSeconds(response: AxiosResponse): number {
    const fromBody = Number(response.data?.retry_after);
    if (response.data?.retry_after != null && !isNaN(fromBody)) return Math.max(0, fromBody);
    const fromHeader = Number(response.headers?.["retry-after"]);
    if (response.headers?.["retry-after"] != null && !isNaN(fromHeader)) return Math.max(0, fromHeader);
    const resetAt = Number(response.headers?.["x-ratelimit-reset"]);
    if (response.headers?.["x-ratelimit-reset"] != null && !isNaN(resetAt)) {
        return Math.max(0, Math.ceil(resetAt - Date.now() / 1000));
    }
    return DEFAULT_RETRY_AFTER_SEC;
}

function tooManyRequestsMessage(seconds: number): string {
    if (seconds < 60) {
        return translate(
            seconds !== 1
                ? "Sorry, you have exceeded the number of tries. Please try again in {seconds} seconds."
                : "Sorry, you have exceeded the number of tries. Please try again in {seconds} second.",
            { seconds }
        );
    }
    const minutes = Math.ceil(seconds / 60);
    return translate(
        minutes !== 1
            ? "Sorry, you have exceeded the number of tries. Please try again in {minutes} minutes."
            : "Sorry, you have exceeded the number of tries. Please try again in {minutes} minute.",
        { minutes }
    );
}

/** What to tell the user about a refusal, or null when this layer says nothing (409 and the rest are the caller's). */
function describeRefusal(status: number, data: any, url: string, retryAfter: number): Notice | null {
    const selfHandled = SELF_HANDLED_URLS.some((fragment) => url.includes(fragment));
    const inlineValidation = INLINE_VALIDATION_URLS.some((fragment) => url.includes(fragment));
    const inlineLimits = INLINE_LIMIT_URLS.some((fragment) => url.startsWith(fragment));

    switch (status) {
        case 400:
        case 422:
            return inlineValidation ? null : { title: translate("Validation Error"), message: refusalMessage(data) };
        case 401:
            return selfHandled || inlineValidation
                ? null
                : { title: translate("Something went wrong"), message: refusalMessage(data) };
        case 403:
            return selfHandled
                ? null
                : { title: translate("Forbidden"), message: inlineValidation ? "" : refusalMessage(data) };
        case 429:
            return {
                title: translate("Too many attempts"),
                message: tooManyRequestsMessage(retryAfter),
                inline: inlineLimits,
            };
        case 500:
        case 502:
            return {
                title: translate("Server Error"),
                message: translate("A server error occurred during request execution"),
                inline: inlineLimits,
            };
        default:
            return null;
    }
}

/**
 * The response error handler both clients share. It shows at most one toast per refusal, marks what it
 * showed as `handled`, and flips `isUnauthorized` on a 401 (the global logout).
 *
 * `silentError` = "the caller handles this error inline": no toast and no logout — a 401 from a
 * scope-gated sip.io-v2 /v1 call (reports, contacts) means "no /v1 access right now", NOT a dead PBX
 * session. The 429 `retryAfter` is still set.
 */
export async function errorInterceptor(error: any) {
    if (error.handled || error?.code === "ERR_CANCELED") {
        return Promise.reject(error);
    }

    const response: AxiosResponse | undefined = error.response;
    const status: number | undefined = response?.status;
    const url: string = response?.config?.url ?? "";
    const silentError = response?.config?.silentError === true;

    if (status === 429 && response) {
        error.retryAfter = retryAfterSeconds(response);
    }

    // CT-301 Verify Card: any charge on an unverified card gets the same "verify your card first" pop-up,
    // even from callers that handle their own errors (like trial_seat_cap below).
    const cardUnverified = isCardUnverifiedError(error) && tryOpenVerifyCardFirst();
    if (cardUnverified) {
        error.handled = true;
    }

    const notice =
        status && !cardUnverified ? describeRefusal(status, response?.data, url, error.retryAfter ?? 0) : null;
    if (notice) {
        error.handled = true;
        if (!silentError && !notice.inline) showToast(notice.message, notice.title);
    }

    // CT-566: a free trial at its user limit. In the dashboard every add gets the same answer, the
    // paid-plan pop-up. Elsewhere (invite accept) the caller shows its own message.
    if (status === 409 && response?.data?.error === "trial_seat_cap" && tryOpenTrialCapDialog()) {
        error.handled = true;
    }
    // CT-609: auto top-up refused on a trial (the screen thought the plan was paid). Same pop-up.
    if (
        status === 409 &&
        response?.data?.error === "plan_not_active" &&
        tryOpenTrialCapDialog({ reason: "autotopup" })
    ) {
        error.handled = true;
    }

    error.errors = notice?.message ?? "";
    error.status = status;

    const selfHandled = SELF_HANDLED_URLS.some((fragment) => url.includes(fragment));
    if (status === 401 && !silentError && !selfHandled) {
        authStore.setState((state) => {
            state.isUnauthorized = true;
        });
    }
    return Promise.reject(error);
}

function showToast(message: string, title: string) {
    toast({
        variant: "destructive",
        title,
        description: React.createElement("pre", { className: "font-sans" }, message),
        isPermanent: true,
    });
}

export const MAX_CHAR_LIMIT = 2000;
export const MAX_TICKET_SUBJECT_LIMIT = 255;
export const MAX_TOTAL_FILE_SIZE = 4 * 1024 * 1024;
export const MAX_FILES_NUMBER = 10;
